import { Component } from 'react';

import BpkText from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import FormField from '../FormField';

import DatePicker from './DatePicker';

import type { I18nService } from '@shared/types/i18n';

import STYLES from './DateRangeSelector.module.scss';

import { withI18n } from '@shared/ui/contexts/i18n';
import {
  applyCheckInDateRestrictions,
  applyCheckOutDateRestrictions,
  stayLength,
} from '@shared/utils-universal/guest-stay';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  checkInDate: Date;
  checkOutDate: Date;
  onDatesChanged: Function;
  className?: string;
  formFieldClassName?: string;
  disabled?: boolean;
  showNights?: boolean;
  showLabel?: boolean;
  lightLabel?: boolean;
  masonry?: boolean;
};

class DateRangeSelector extends Component<Props> {
  handleCheckInChange = (newCheckInDate: Date) => {
    const { checkOutDate, onDatesChanged } = this.props;

    const newCheckOutDate = applyCheckOutDateRestrictions(
      newCheckInDate,
      checkOutDate,
    );
    onDatesChanged(newCheckInDate, newCheckOutDate);
  };

  handleCheckOutChange = (newCheckOutDate: Date) => {
    const { checkInDate, onDatesChanged } = this.props;

    const newCheckInDate = applyCheckInDateRestrictions(
      checkInDate,
      newCheckOutDate,
    );
    onDatesChanged(newCheckInDate, newCheckOutDate);
  };

  render() {
    const {
      checkInDate,
      checkOutDate,
      className,
      disabled,
      formFieldClassName,
      i18n: { translate, translatePlural },
      lightLabel,
      masonry,
      showLabel,
      showNights,
    } = this.props;
    const inputProps = {
      className: cls(!showNights && 'DateRangeSelector__input'),
      disabled,
      readOnly: true,
    };

    const checkInFieldId = `checkin${masonry ? '-masonry' : ''}`;
    const checkOutFieldId = `checkout${masonry ? '-masonry' : ''}`;
    const checkInLabel = translate('SearchControls_label_CheckIn');
    const checkOutLabel = translate('SearchControls_label_CheckOut');

    return (
      <div
        className={cls(
          'DateRangeSelector',
          className,
          showNights && 'DateRangeSelector__showNights',
        )}
      >
        <FormField
          fieldId={checkInFieldId}
          label={showLabel ? checkInLabel : undefined}
          lightLabel={lightLabel}
          className={cls(
            formFieldClassName,
            showNights && 'DateRangeSelector__checkInForm',
          )}
        >
          <DatePicker
            id={checkInFieldId}
            onDateSelect={this.handleCheckInChange}
            startDate={checkInDate}
            endDate={checkOutDate}
            date={checkInDate}
            inputProps={{
              ...inputProps,
              placeholder: checkInLabel,
              'data-testid': 'checkin-selector',
            }}
            popperModifiers={[
              {
                name: 'flip',
                options: { enabled: false },
              },
            ]}
            isCheckOut={false}
          />
        </FormField>
        {showNights && (
          <div className={cls('DateRangeSelector__nights')}>
            <BpkText>
              {translatePlural(
                'SelectionSummary_label_nights',
                stayLength({ checkIn: checkInDate, checkOut: checkOutDate }),
                'number',
              )}
            </BpkText>
          </div>
        )}
        <FormField
          fieldId={checkOutFieldId}
          label={showLabel ? checkOutLabel : undefined}
          lightLabel={lightLabel}
          className={cls(
            formFieldClassName,
            showNights && 'DateRangeSelector__checkoutForm',
          )}
        >
          <DatePicker
            id={checkOutFieldId}
            onDateSelect={this.handleCheckOutChange}
            startDate={checkInDate}
            endDate={checkOutDate}
            date={checkOutDate}
            inputProps={{
              ...inputProps,
              placeholder: checkOutLabel,
              'data-testid': 'checkout-selector',
            }}
            popperModifiers={[
              {
                name: 'flip',
                options: { enabled: false },
              },
            ]}
            isCheckOut
          />
        </FormField>
      </div>
    );
  }
}

export default withI18n(DateRangeSelector);
